import content from "./content.json";

export const FREQUENCY_CAP = 2;
export const MATCH_THRESHOLD = 0.6;

const createThread = () => ({
  turns: [],
  asked: new Set(),
  appearances: {},
  sponsoredBrand: null,
  sponsoredAsked: new Set(),
  busy: false,
  sequence: 0,
});

const unique = (values) => [...new Set(values)];

export class ConversationEngine {
  constructor(environment) {
    if (!content.envs[environment]) {
      throw new Error(`Unknown conversational environment: ${environment}`);
    }

    this.environment = environment;
    this.reset();
  }

  reset() {
    this.thread = createThread();
    this.openingDecision = this.createOpeningDecision();
  }

  createOpeningDecision() {
    const creativeId = content.preTurnPrompt[this.environment];
    const creative = content.creatives[creativeId];
    const used = this.thread.appearances[creative.brand] || 0;

    if (used >= FREQUENCY_CAP) {
      return null;
    }

    this.thread.appearances[creative.brand] = used + 1;
    const decision = {
      turn: 0,
      kind: "pre",
      environment: this.environment,
      brand: creative.brand,
      served: [creativeId],
      status: "served",
    };
    this.thread.turns.push(decision);
    return decision;
  }

  getOpening() {
    return {
      greeting:
        this.environment === "ehr"
          ? "Hello! I'm your Clinical Assistant. I can help with dosing, guidelines and evidence for the patient in front of you. How can I assist you today?"
          : "Hi Dr. Collins. Ask me anything about what you're reading on Rounds Review, from new guidelines to trial results.",
      questionKeys: content.starters[this.environment],
      promptCreativeId: this.openingDecision
        ? this.openingDecision.served[0]
        : null,
    };
  }

  routeQuestion(text) {
    const normalized = text.toLowerCase();

    for (const route of content.routes) {
      const matchesAll =
        !route.all ||
        route.all.every((group) =>
          group.split("|").some((keyword) => normalized.includes(keyword))
        );
      const matchesAny =
        !route.any ||
        route.any.some((keyword) => normalized.includes(keyword));

      if (matchesAll && matchesAny) {
        return route.q;
      }
    }

    return null;
  }

  routeSponsoredQuestion(brand, text) {
    const approved = content.sponsored[brand].answers;
    const match = content.sponsoredRoutes.find(
      ({ pattern, flags, topic }) =>
        approved[topic] && new RegExp(pattern, flags).test(text.toLowerCase())
    );
    return match ? match.topic : null;
  }

  beginQuestion(questionKey) {
    const question = content.questions[questionKey];
    return this.beginOrganicTurn(questionKey, question.text);
  }

  beginTypedQuestion(text) {
    if (this.thread.sponsoredBrand) {
      const brand = this.thread.sponsoredBrand;
      return this.beginSponsoredTurn(
        brand,
        this.routeSponsoredQuestion(brand, text),
        text
      );
    }

    return this.beginOrganicTurn(this.routeQuestion(text), text);
  }

  beginOrganicTurn(questionKey, questionText) {
    if (this.thread.busy) {
      return null;
    }

    const exitedSponsored = Boolean(this.thread.sponsoredBrand);
    if (exitedSponsored) {
      this.clearSponsoredConversation();
    }

    const answer = questionKey
      ? content.answers[content.questions[questionKey].answer]
      : content.answers.A_FALLBACK;

    if (questionKey) {
      this.thread.asked.add(questionKey);
    }

    this.thread.busy = true;
    return {
      id: ++this.thread.sequence,
      kind: "organic",
      questionKey,
      questionText,
      answer,
      exitedSponsored,
    };
  }

  beginPrompt(creativeId) {
    if (this.thread.busy) {
      return null;
    }

    const creative = content.creatives[creativeId];
    if (!creative || creative.format !== "prompt") {
      return null;
    }

    if (this.thread.sponsoredBrand) {
      this.clearSponsoredConversation();
    }

    this.thread.sponsoredBrand = creative.sp;
    this.thread.sponsoredAsked = new Set();
    return this.beginSponsoredTurn(
      creative.sp,
      creative.entry,
      creative.text,
      true
    );
  }

  beginSponsoredTopic(topic) {
    if (!this.thread.sponsoredBrand || this.thread.busy) {
      return null;
    }

    const brand = this.thread.sponsoredBrand;
    const answer = content.sponsored[brand].answers[topic];
    return this.beginSponsoredTurn(brand, topic, answer.q);
  }

  beginSponsoredTurn(brand, topic, questionText, entered = false) {
    if (this.thread.busy) {
      return null;
    }

    this.thread.busy = true;
    if (topic) {
      this.thread.sponsoredAsked.add(topic);
    }

    return {
      id: ++this.thread.sequence,
      kind: "sponsored",
      brand,
      topic,
      questionText,
      answer: topic ? content.sponsored[brand].answers[topic] : null,
      entered,
    };
  }

  completeTurn(turn) {
    if (turn.kind === "sponsored") {
      const decision = {
        turn: turn.id,
        kind: "sponsored",
        environment: this.environment,
        brand: turn.brand,
        served: [],
        status: turn.answer ? "sponsored" : "outside",
      };
      this.thread.turns.push(decision);

      return {
        decision,
        questionKeys: content.sponsored[turn.brand].followups.filter(
          (topic) =>
            !this.thread.sponsoredAsked.has(topic) &&
            content.sponsored[turn.brand].answers[topic]
        ),
        promptCreativeId: null,
      };
    }

    const decision = this.decide(turn.answer, turn.id);
    this.thread.turns.push(decision);
    return {
      decision,
      ...this.getOrganicSuggestions(turn.answer, decision),
    };
  }

  finishTurn() {
    this.thread.busy = false;
  }

  decide(answer, turn) {
    const decision = {
      turn,
      kind: "organic",
      environment: this.environment,
      area: answer.intent.area,
      served: [],
    };

    if (answer.blocked) {
      decision.status = "blocked";
      return decision;
    }

    const topCandidate = (answer.cand || [])
      .filter(([, score]) => score >= MATCH_THRESHOLD)
      .sort((a, b) => b[1] - a[1])[0];

    if (!topCandidate) {
      decision.status = "nomatch";
      return decision;
    }

    const [brand] = topCandidate;
    const used = this.thread.appearances[brand] || 0;
    const plan = (answer.ads[this.environment] || []).filter(
      (creativeId) => content.creatives[creativeId].brand === brand
    );

    decision.brand = brand;
    if (used >= FREQUENCY_CAP) {
      decision.status = "capped";
    } else if (!plan.length) {
      decision.status = "nomatch";
    } else {
      this.thread.appearances[brand] = used + 1;
      decision.served = plan;
      decision.status = "served";
    }

    return decision;
  }

  getOrganicSuggestions(answer, decision) {
    const preferred =
      (answer.followups && answer.followups[this.environment]) || [];
    const questionKeys = this.getAvailableQuestionKeys(preferred);

    return {
      questionKeys,
      promptCreativeId:
        decision.served.find(
          (creativeId) =>
            content.creatives[creativeId].format === "prompt"
        ) || null,
    };
  }

  getAvailableQuestionKeys(preferred = []) {
    return unique([
      ...preferred,
      ...content.starters[this.environment],
      ...Object.keys(content.questions),
    ])
      .filter(
        (key) =>
          !this.thread.asked.has(key) &&
          (!content.questions[key].only ||
            content.questions[key].only === this.environment)
      )
      .slice(0, 3);
  }

  exitSponsoredConversation() {
    if (!this.thread.sponsoredBrand || this.thread.busy) {
      return null;
    }

    const brand = this.thread.sponsoredBrand;
    this.clearSponsoredConversation();
    return brand;
  }

  clearSponsoredConversation() {
    this.thread.sponsoredBrand = null;
    this.thread.sponsoredAsked = new Set();
  }

  get sponsoredBrand() {
    return this.thread.sponsoredBrand;
  }

  get busy() {
    return this.thread.busy;
  }
}

export { content };
